// File: ManageCurriculum.js
import React, { useEffect, useRef, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Row, Table, Dropdown, Col, Button, Nav, Modal, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getToken } from '../services/authorize'
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from '../contexts/LoadingContext';

const EMPTY_SUB_FORM = { sub_id: null, sub_subid: "", sub_subname: "", sub_credit: "", sub_semester: "1", sub_type: "basic", sub_order: "" };

export default function ManageCurriculum() {
  ///// Skin Switch /////
  const currentSkin = (localStorage.getItem('skin-mode')) ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);

  const switchSkin = (skin) => {
    if (skin === 'dark') {
      const btnWhite = document.getElementsByClassName('btn-white');
      for (const btn of btnWhite) {
        btn.classList.add('btn-outline-primary');
        btn.classList.remove('btn-white');
      }
    } else {
      const btnOutlinePrimary = document.getElementsByClassName('btn-outline-primary');
      for (const btn of btnOutlinePrimary) {
        btn.classList.remove('btn-outline-primary');
        btn.classList.add('btn-white');
      }
    }
    const tables = document.getElementsByTagName('table');
    for (const table of tables) {
      const theads = table.querySelectorAll('thead');
      if (skin === 'dark') {
        for (const thead of theads) {
          thead.classList.remove('table-secondary');
          thead.classList.add('table-primary');
        }
        table.classList.add('table-dark');
      } else {
        table.classList.remove('table-dark');
        for (const thead of theads) {
          thead.classList.remove('table-primary');
          thead.classList.add('table-secondary');
        }
      }
    }
  }

  switchSkin(skin);
  useEffect(() => { switchSkin(skin); }, [skin]);

  const { setLoading } = useLoading();
  const [yearId, setYearId] = useState(() => localStorage.getItem('yearId') || null);
  const [selectedYear, setSelectedYear] = useState("");
  const [yearList, setYearList] = useState([]);
  const [curriculumList, setCurriculumList] = useState([]);
  const [assignSyslabusData, setAssignSyllabusData] = useState([]);

  useEffect(() => {
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/getStudyYear`, {
      headers: { authorization: `Bearer ${getToken()}` }
    })
      .then(res => {
        const list = res.data;
        setYearList(list);
        let initialYearId = localStorage.getItem('yearId');
        if (!initialYearId && list.length > 0) {
          initialYearId = list[0].year_id;
          localStorage.setItem('yearId', initialYearId);
        }
        setYearId(initialYearId);
        const selected = list.find(y => String(y.year_id) === String(initialYearId));
        setSelectedYear(selected ? `ปีการศึกษา ${selected.year_year}` : "");
      })
      .catch(err => console.log("Error loading years:", err))
      .finally(() => setLoading(false));
  }, [setLoading]);

  const handleAddStudyYear = () => {
    Swal.fire({
      title: 'เริ่มปีการศึกษาใหม่',
      text: 'กรุณาระบุปีการศึกษา (เช่น 2568)',
      input: 'text',
      inputPlaceholder: 'ปีการศึกษา',
      showCancelButton: true,
      confirmButtonText: 'บันทึก',
      cancelButtonText: 'ยกเลิก',
      inputValidator: (value) => {
        if (!value || !value.trim()) return 'กรุณาระบุปีการศึกษา';
        if (!/^\d{4}$/.test(value.trim())) return 'ปีการศึกษาต้องเป็นตัวเลข 4 หลัก';
      }
    }).then(result => {
      if (!result.isConfirmed) return;
      setLoading(true);
      axiosInstance.post(`${process.env.REACT_APP_API}/addStudyYear`, { year_year: result.value.trim() }, {
        headers: { authorization: `Bearer ${getToken()}` }
      })
        .then(res => {
          const newYear = { year_id: res.data.year_id, year_year: res.data.year_year };
          setYearList(prev => [newYear, ...prev]);
          setYearId(newYear.year_id);
          setSelectedYear(`ปีการศึกษา ${newYear.year_year}`);
          localStorage.setItem('yearId', newYear.year_id);
          setSelectedAssign(null);
          setSubjects([]);
          setLoading(false);
          Swal.fire({ icon: 'success', title: 'สำเร็จ', text: res.data.message, timer: 1500, showConfirmButton: false });
        })
        .catch(err => {
          setLoading(false);
          Swal.fire({ icon: 'error', title: err.response?.data?.title || 'เกิดข้อผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถเพิ่มปีการศึกษาได้' });
        });
    });
  };

  const handleSelectYear = (id) => {
    const selected = yearList.find(y => y.year_id === id);
    if (selected) {
      setYearId(id);
      setSelectedYear(`ปีการศึกษา ${selected.year_year}`);
      localStorage.setItem('yearId', id);
      setSelectedAssign(null);
      setSubjects([]);
    }
  }

  useEffect(() => {
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/getCurriculum`, {
      headers: { authorization: `Bearer ${getToken()}` }
    })
      .then(res => setCurriculumList(res.data))
      .catch(() => Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: 'ไม่สามารถโหลดข้อมูลหลักสูตรได้' }))
      .finally(() => setLoading(false));
  }, [setLoading]);

  useEffect(() => {
    if (yearId) {
      setLoading(true);
      axiosInstance.get(`${process.env.REACT_APP_API}/getAssignSyllabus/${yearId}`, {
        headers: { authorization: `Bearer ${getToken()}` }
      })
        .then(res => setAssignSyllabusData(res.data))
        .catch(() => Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: 'ไม่สามารถโหลดข้อมูลหลักสูตรที่กำหนดได้' }))
        .finally(() => setLoading(false));
    }
  }, [yearId, setLoading]);

  const [classList, setClassList] = useState([]);
  useEffect(() => {
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/getClassList`, {
      headers: { authorization: `Bearer ${getToken()}` }
    })
      .then(res => setClassList(res.data))
      .catch(() => Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: 'ไม่สามารถโหลดข้อมูลชั้นเรียนได้' }))
      .finally(() => setLoading(false));
  }, [setLoading]);

  const [programList, setProgramList] = useState([]);
  useEffect(() => {
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/getProgramList`, {
      headers: { authorization: `Bearer ${getToken()}` }
    })
      .then(res => setProgramList(res.data))
      .catch(() => Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: 'ไม่สามารถโหลดข้อมูลโปรแกรมได้' }))
      .finally(() => setLoading(false));
  }, [setLoading]);

  const handleDeleteCurriculum = (syl_id) => {
    Swal.fire({
      title: 'ยืนยันการลบหลักสูตร', text: "คุณต้องการลบหลักสูตรนี้หรือไม่?",
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#d33', cancelButtonColor: '#3085d6',
      confirmButtonText: 'ลบ', cancelButtonText: 'ยกเลิก'
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);
        axiosInstance.post(`${process.env.REACT_APP_API}/deleteCurriculum`, { syl_id }, {
          headers: { authorization: `Bearer ${getToken()}` }
        })
          .then(res => {
            setCurriculumList(curriculumList.filter(item => item.syl_id !== syl_id));
            setLoading(false);
            Swal.fire({ icon: 'success', title: 'ลบหลักสูตรสำเร็จ', text: res.data.message });
          })
          .catch(err => {
            setLoading(false);
            Swal.fire({ icon: 'error', title: err.response?.data?.title || 'เกิดข้อผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถลบหลักสูตรได้' });
          });
      }
    });
  }

  const handleAddCurriculum = () => {
    Swal.fire({
      title: 'เพิ่มหลักสูตรใหม่', text: "กรุณาระบุปีการศึกษาสำหรับหลักสูตรใหม่",
      input: 'text', inputPlaceholder: 'ปีการศึกษา (เช่น 2566)',
      showCancelButton: true, confirmButtonText: 'เพิ่ม', cancelButtonText: 'ยกเลิก'
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        const newYear = result.value.trim();
        if (!newYear) { Swal.fire('กรุณาระบุปีการศึกษา'); return; }
        setLoading(true);
        axiosInstance.post(`${process.env.REACT_APP_API}/addSyllabus`, { syl_year: newYear }, {
          headers: { authorization: `Bearer ${getToken()}` }
        })
          .then(res => {
            Swal.fire({ icon: 'success', title: 'เพิ่มหลักสูตรสำเร็จ', text: res.data.message });
            setCurriculumList([...curriculumList, { syl_id: res.data.syl_id, syl_year: newYear }]);
          })
          .catch(err => Swal.fire({ icon: 'error', title: err.response?.data?.title || 'เกิดข้อผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถเพิ่มหลักสูตรได้' }))
          .finally(() => setLoading(false));
      }
    });
  }

  const [selectedClass, setSelectedClass] = useState("");
  const [selectedProgram, setSelectedProgram] = useState("");
  const [selectedSyllabus, setSelectedSyllabus] = useState("");
  const [modalMode, setModalMode] = useState("add");
  const [editAssignId, setEditAssignId] = useState(null);
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);

  const handleShowModal = (mode, as_id) => {
    setShow(false);
    setModalMode(mode);
    if (mode === "add") {
      setEditAssignId(null);
    } else {
      setEditAssignId(as_id);
    }
    setShow(true);
  }

  useEffect(() => {
    if (modalMode === "edit" && editAssignId) {
      const row = assignSyslabusData.find(d => String(d.as_id) === String(editAssignId));
      if (row) {
        setSelectedClass(row.c_id);
        setSelectedProgram(row.p_id);
        setSelectedSyllabus(row.syl_id);
      }
    }
    if (modalMode === "add") {
      setSelectedClass("");
      setSelectedProgram("");
      setSelectedSyllabus("");
    }
    // eslint-disable-next-line
  }, [modalMode, editAssignId, assignSyslabusData]);

  const handleSaveAssignSyllabus = () => {
    if (!yearId) { Swal.fire('กรุณาเลือกปีการศึกษาก่อน'); return; }
    if (!selectedClass || !selectedProgram || !selectedSyllabus) { Swal.fire('กรุณากรอกข้อมูลให้ครบถ้วน'); return; }
    setLoading(true);
    if (modalMode === "add") {
      axiosInstance.post(`${process.env.REACT_APP_API}/assignSyllabus`,
        { year_id: yearId, class_id: selectedClass, program_id: selectedProgram, syllabus_id: selectedSyllabus },
        { headers: { authorization: `Bearer ${getToken()}` } }
      )
        .then(res => {
          Swal.fire({ icon: 'success', title: 'สำเร็จ', text: res.data.message });
          setAssignSyllabusData([...assignSyslabusData, {
            as_id: res.data.newData.as_id, c_id: res.data.newData.c_id, c_class: res.data.newData.c_class,
            p_id: res.data.newData.p_id, p_code: res.data.newData.p_code,
            syl_id: res.data.newData.syl_id, syl_year: res.data.newData.syl_year,
            subject_count: res.data.newData.subject_count
          }]);
        })
        .catch(err => Swal.fire({ icon: 'error', title: err.response?.data?.title || 'เกิดข้อผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถกำหนดหลักสูตรได้' }))
        .finally(() => { setSelectedClass(""); setSelectedProgram(""); setSelectedSyllabus(""); setEditAssignId(null); setShow(false); setLoading(false); });
    } else {
      axiosInstance.post(`${process.env.REACT_APP_API}/editAssignSyllabus`,
        { as_id: editAssignId, year_id: yearId, class_id: selectedClass, program_id: selectedProgram, syllabus_id: selectedSyllabus },
        { headers: { authorization: `Bearer ${getToken()}` } }
      )
        .then(res => {
          Swal.fire({ icon: 'success', title: 'แก้ไขสำเร็จ', text: res.data.message });
          setAssignSyllabusData(assignSyslabusData.map(item =>
            item.as_id === editAssignId ? {
              ...item, c_id: res.data.newData.c_id, c_class: res.data.newData.c_class,
              p_id: res.data.newData.p_id, p_code: res.data.newData.p_code,
              syl_id: res.data.newData.syl_id, syl_year: res.data.newData.syl_year,
              subject_count: res.data.newData.subject_count
            } : item
          ));
        })
        .catch(err => Swal.fire({ icon: 'error', title: err.response?.data?.title || 'เกิดข้อผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถแก้ไขหลักสูตรได้' }))
        .finally(() => { setSelectedClass(""); setSelectedProgram(""); setSelectedSyllabus(""); setShow(false); setLoading(false); });
    }
  };

  const handleDeleteAssignSyllabus = (as_id) => {
    Swal.fire({
      title: 'ยืนยันการลบการกำหนดหลักสูตร', text: "คุณต้องการลบการกำหนดหลักสูตรนี้หรือไม่?",
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#d33', cancelButtonColor: '#3085d6',
      confirmButtonText: 'ลบ', cancelButtonText: 'ยกเลิก'
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);
        axiosInstance.post(`${process.env.REACT_APP_API}/deleteAssignSyllabus`, { as_id }, {
          headers: { authorization: `Bearer ${getToken()}` }
        })
          .then(res => {
            setAssignSyllabusData(assignSyslabusData.filter(item => item.as_id !== as_id));
            if (selectedAssign?.as_id === as_id) { setSelectedAssign(null); setSubjects([]); }
            setLoading(false);
            Swal.fire({ icon: 'success', title: 'ลบการกำหนดหลักสูตรสำเร็จ', text: res.data.message });
          })
          .catch(err => {
            setLoading(false);
            Swal.fire({ icon: 'error', title: err.response?.data?.title || 'เกิดข้อผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถลบการกำหนดหลักสูตรได้' });
          });
      }
    });
  }

  // ---------- Subject management ----------
  const subjectPanelRef = useRef(null);
  const [selectedAssign, setSelectedAssign] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [subjectLoading, setSubjectLoading] = useState(false);
  const [subjectModalShow, setSubjectModalShow] = useState(false);
  const [subjectModalMode, setSubjectModalMode] = useState("add");
  const [subForm, setSubForm] = useState(EMPTY_SUB_FORM);
  const [importModalShow, setImportModalShow] = useState(false);
  const [importSourceSylId, setImportSourceSylId] = useState("");

  const loadSubjects = (assign) => {
    setSubjectLoading(true);
    const prog = assign.p_id ?? 'null';
    axiosInstance.get(
      `${process.env.REACT_APP_API}/getSubjectsBySyllabus/${assign.syl_id}/${assign.c_id}/${prog}`,
      { headers: { authorization: `Bearer ${getToken()}` } }
    )
      .then(res => setSubjects(res.data))
      .catch(() => Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: 'ไม่สามารถโหลดรายวิชาได้' }))
      .finally(() => setSubjectLoading(false));
  };

  const handleSelectAssign = (data) => {
    setSelectedAssign(data);
    loadSubjects(data);
    setTimeout(() => {
      subjectPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleOpenSubjectModal = (mode, subject = null) => {
    setSubjectModalMode(mode);
    if (mode === "add") {
      setSubForm(EMPTY_SUB_FORM);
    } else {
      setSubForm({
        sub_id: subject.sub_id,
        sub_subid: subject.sub_subid,
        sub_subname: subject.sub_subname,
        sub_credit: subject.sub_credit,
        sub_semester: String(subject.sub_semester),
        sub_type: subject.sub_type,
        sub_order: subject.sub_order ?? ""
      });
    }
    setSubjectModalShow(true);
  };

  const handleSaveSubject = () => {
    if (!subForm.sub_subid || !subForm.sub_subname || subForm.sub_credit === "") {
      Swal.fire('กรุณากรอกข้อมูลให้ครบถ้วน');
      return;
    }
    setLoading(true);
    const payload = {
      sub_subid: subForm.sub_subid,
      sub_subname: subForm.sub_subname,
      sub_credit: Number(subForm.sub_credit),
      sub_semester: Number(subForm.sub_semester),
      sub_type: subForm.sub_type,
      sub_order: subForm.sub_order !== "" ? Number(subForm.sub_order) : null
    };
    if (subjectModalMode === "add") {
      axiosInstance.post(`${process.env.REACT_APP_API}/addSubject`,
        { ...payload, syl_id: selectedAssign.syl_id, class_id: selectedAssign.c_id, program_id: selectedAssign.p_id || null },
        { headers: { authorization: `Bearer ${getToken()}` } }
      )
        .then(() => {
          Swal.fire({ icon: 'success', title: 'เพิ่มรายวิชาสำเร็จ', timer: 1500, showConfirmButton: false });
          setSubjectModalShow(false);
          loadSubjects(selectedAssign);
          setAssignSyllabusData(assignSyslabusData.map(item =>
            item.as_id === selectedAssign.as_id
              ? { ...item, subject_count: (item.subject_count || 0) + 1 }
              : item
          ));
        })
        .catch(err => Swal.fire({ icon: 'error', title: err.response?.data?.title || 'เกิดข้อผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถเพิ่มรายวิชาได้' }))
        .finally(() => setLoading(false));
    } else {
      axiosInstance.post(`${process.env.REACT_APP_API}/updateSubject`,
        { ...payload, sub_id: subForm.sub_id },
        { headers: { authorization: `Bearer ${getToken()}` } }
      )
        .then(() => {
          Swal.fire({ icon: 'success', title: 'แก้ไขรายวิชาสำเร็จ', timer: 1500, showConfirmButton: false });
          setSubjectModalShow(false);
          loadSubjects(selectedAssign);
        })
        .catch(err => Swal.fire({ icon: 'error', title: err.response?.data?.title || 'เกิดข้อผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถแก้ไขรายวิชาได้' }))
        .finally(() => setLoading(false));
    }
  };

  const handleImportSubjects = () => {
    if (!importSourceSylId) { Swal.fire('กรุณาเลือกหลักสูตรที่ต้องการนำเข้า'); return; }
    setLoading(true);
    axiosInstance.post(`${process.env.REACT_APP_API}/importSubjectsFromSyllabus`, {
      sourceSylId: importSourceSylId,
      targetSylId: selectedAssign.syl_id,
      classId: selectedAssign.c_id,
      programId: selectedAssign.p_id || null
    }, { headers: { authorization: `Bearer ${getToken()}` } })
      .then(res => {
        setImportModalShow(false);
        setImportSourceSylId("");
        loadSubjects(selectedAssign);
        setAssignSyllabusData(prev => prev.map(item =>
          item.as_id === selectedAssign.as_id
            ? { ...item, subject_count: (item.subject_count || 0) + res.data.imported }
            : item
        ));
        Swal.fire({ icon: 'success', title: 'สำเร็จ', text: res.data.message, timer: 2000, showConfirmButton: false });
      })
      .catch(err => Swal.fire({ icon: 'error', title: err.response?.data?.title || 'เกิดข้อผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถนำเข้ารายวิชาได้' }))
      .finally(() => setLoading(false));
  };

  const handleDeleteSubject = (sub_id) => {
    Swal.fire({
      title: 'ยืนยันการลบรายวิชา', text: 'คุณต้องการลบรายวิชานี้หรือไม่?',
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#d33', cancelButtonColor: '#3085d6',
      confirmButtonText: 'ลบ', cancelButtonText: 'ยกเลิก'
    }).then(result => {
      if (result.isConfirmed) {
        setLoading(true);
        axiosInstance.post(`${process.env.REACT_APP_API}/deleteSubject`, { sub_id }, {
          headers: { authorization: `Bearer ${getToken()}` }
        })
          .then(() => {
            setSubjects(subjects.filter(s => s.sub_id !== sub_id));
            setAssignSyllabusData(assignSyslabusData.map(item =>
              item.as_id === selectedAssign.as_id
                ? { ...item, subject_count: Math.max(0, (item.subject_count || 1) - 1) }
                : item
            ));
            setLoading(false);
            Swal.fire({ icon: 'success', title: 'ลบรายวิชาสำเร็จ', timer: 1500, showConfirmButton: false });
          })
          .catch(err => {
            setLoading(false);
            Swal.fire({ icon: 'error', title: err.response?.data?.title || 'เกิดข้อผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถลบรายวิชาได้' });
          });
      }
    });
  };

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">ทะเบียนและหลักสูตร</Link></li>
              <li className="breadcrumb-item active" aria-current="page">จัดการหลักสูตร</li>
            </ol>
            <h4 className="main-title mb-0">จัดการหลักสูตร</h4>
          </div>
          <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
            <Button variant="outline-success" onClick={handleAddStudyYear}>
              <i className="ri-calendar-2-line"></i> เริ่มปีการศึกษาใหม่
            </Button>
            <Dropdown>
              <Dropdown.Toggle variant="primary" id="dropdown-basic">
                {selectedYear || "เลือกปีการศึกษา"}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {yearList.map(item => (
                  <Dropdown.Item key={item.year_id} onClick={() => handleSelectYear(item.year_id)}>
                    ปีการศึกษา {item.year_year}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        {/* Curriculum + Assign rows */}
        <Row className="g-3 mb-4">
          <Col xl="4">
            <Card className="card-one">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <Card.Title as="h6" className="mb-0">หลักสูตร</Card.Title>
                <Nav className="nav-icon nav-icon-sm ms-auto">
                  <Button variant="success" size="sm" onClick={() => handleAddCurriculum()}>
                    <i className="ri-add-line"></i> เพิ่มหลักสูตร
                  </Button>
                </Nav>
              </Card.Header>
              <Card.Body className="p-3">
                <Table responsive bordered hover className="mb-0">
                  <thead>
                    <tr>
                      <th className="text-center">ชื่อหลักสูตร</th>
                      <th className="text-center" style={{ width: 70 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {curriculumList.length > 0 ? curriculumList.map((data) => (
                      <tr key={data.syl_id}>
                        <td className="text-center">หลักสูตรปีการศึกษา {data.syl_year}</td>
                        <td className="text-center">
                          <Button variant="danger" size="sm" className="btn-xs" onClick={() => handleDeleteCurriculum(data.syl_id)}>
                            <i className="ri-close-line"></i> ลบ
                          </Button>
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan={2} className="text-center">ไม่พบข้อมูล</td></tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>

          <Col xl="8">
            <Card className="card-one">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <Card.Title as="h6" className="mb-0">กำหนดหลักสูตรของ{selectedYear}</Card.Title>
                <Nav className="nav-icon nav-icon-sm ms-auto">
                  <Button variant="primary" size="sm" onClick={() => handleShowModal("add")}>
                    <i className="ri-draft-line"></i> กำหนดหลักสูตร
                  </Button>
                </Nav>
              </Card.Header>
              <Card.Body className="p-3">
                {yearId ? (
                  <Table responsive bordered hover className="mb-0">
                    <thead>
                      <tr>
                        <th className="text-center">ชั้น</th>
                        <th className="text-center">โปรแกรม</th>
                        <th className="text-center">หลักสูตรที่ใช้</th>
                        <th className="text-center" style={{ width: 130 }}>จำนวนวิชา</th>
                        <th className="text-center" style={{ width: 180 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {assignSyslabusData.length > 0 ? assignSyslabusData.map((data) => (
                        <tr
                          key={data.as_id}
                          className={selectedAssign?.as_id === data.as_id ? "table-active" : ""}
                        >
                          <td className="text-center">{data.c_class}</td>
                          <td className="text-center">{data.p_code}</td>
                          <td className="text-center">หลักสูตรปี {data.syl_year}</td>
                          <td className="text-center">{data.subject_count}</td>
                          <td className="text-center">
                            <Button variant="info" size="sm" className="btn-xs text-white" onClick={() => handleSelectAssign(data)}>
                              <i className="ri-book-2-line"></i> รายวิชา
                            </Button>
                            <Button variant="secondary" size="sm" className="btn-xs ms-1" onClick={() => handleShowModal("edit", data.as_id)}>
                              <i className="ri-edit-line"></i>
                            </Button>
                            <Button variant="danger" size="sm" className="btn-xs ms-1" onClick={() => handleDeleteAssignSyllabus(data.as_id)}>
                              <i className="ri-close-line"></i>
                            </Button>
                          </td>
                        </tr>
                      )) : (
                        <tr><td colSpan={5} className="text-center">ไม่พบข้อมูลหลักสูตร</td></tr>
                      )}
                    </tbody>
                  </Table>
                ) : (
                  <p>กรุณาเลือกปีการศึกษาเพื่อดูรายละเอียดหลักสูตร</p>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Subject panel */}
        {selectedAssign && (
          <Row className="g-3 mb-4" ref={subjectPanelRef}>
            <Col xl="12">
              <Card className="card-one">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <Card.Title as="h6" className="mb-0">
                    รายวิชา —&nbsp;
                    <span className="text-primary">{selectedAssign.c_class}</span>
                    {selectedAssign.p_code && <span className="text-muted"> ({selectedAssign.p_code})</span>}
                    <span className="text-muted fw-normal ms-1">หลักสูตรปี {selectedAssign.syl_year}</span>
                    <span className="text-muted fw-normal ms-1 fs-sm">({subjects.length} วิชา)</span>
                  </Card.Title>
                  <div className="d-flex gap-2">
                    <Button variant="outline-primary" size="sm" onClick={() => { setImportSourceSylId(""); setImportModalShow(true); }}>
                      <i className="ri-file-copy-line"></i> นำเข้าจากปีที่แล้ว
                    </Button>
                    <Button variant="success" size="sm" onClick={() => handleOpenSubjectModal("add")}>
                      <i className="ri-add-line"></i> เพิ่มรายวิชา
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={() => { setSelectedAssign(null); setSubjects([]); }}>
                      <i className="ri-close-line"></i>
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  {subjectLoading ? (
                    <div className="text-center py-4 text-muted">กำลังโหลดรายวิชา...</div>
                  ) : subjects.length === 0 ? (
                    <div className="text-center py-4 text-muted">ยังไม่มีรายวิชา กดปุ่ม "เพิ่มรายวิชา" เพื่อเริ่มต้น</div>
                  ) : (
                    <Table hover responsive className="mb-0 align-middle">
                      <thead className="table-secondary">
                        <tr>
                          <th className="text-center" style={{ width: 60 }}>ลำดับ</th>
                          <th style={{ width: 130 }}>รหัสวิชา</th>
                          <th>ชื่อวิชา</th>
                          <th className="text-center" style={{ width: 90 }}>หน่วยกิต</th>
                          <th className="text-center" style={{ width: 100 }}>ภาคเรียน</th>
                          <th className="text-center" style={{ width: 110 }}>ประเภท</th>
                          <th className="text-center" style={{ width: 100 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {subjects.map((sub, idx) => (
                          <tr key={sub.sub_id}>
                            <td className="text-center text-muted">{sub.sub_order ?? idx + 1}</td>
                            <td>{sub.sub_subid}</td>
                            <td>{sub.sub_subname}</td>
                            <td className="text-center">{sub.sub_credit}</td>
                            <td className="text-center">{sub.sub_semester}</td>
                            <td className="text-center">
                              <span className={`badge ${sub.sub_type === 'basic' ? 'bg-primary' : 'bg-warning text-dark'}`}>
                                {sub.sub_type === 'basic' ? 'พื้นฐาน' : 'เพิ่มเติม'}
                              </span>
                            </td>
                            <td className="text-center">
                              <Button variant="secondary" size="sm" className="btn-xs" onClick={() => handleOpenSubjectModal("edit", sub)}>
                                <i className="ri-edit-line"></i>
                              </Button>
                              <Button variant="danger" size="sm" className="btn-xs ms-1" onClick={() => handleDeleteSubject(sub.sub_id)}>
                                <i className="ri-delete-bin-line"></i>
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        <Footer />
      </div>

      {/* Assign syllabus Modal */}
      <Modal show={show} backdrop="static" size="sm" onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="ri-draft-line"></i> {modalMode === "add" ? "กำหนดหลักสูตร" : "แก้ไขการกำหนดหลักสูตร"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={e => e.preventDefault()}>
            <div className="mb-3">
              <label className="form-label">ปีการศึกษา</label>
              <input type="text" className="form-control" value={selectedYear} readOnly />
            </div>
            <div className="mb-3">
              <label className="form-label">ชั้น</label>
              <select className="form-select" value={String(selectedClass)} onChange={e => setSelectedClass(e.target.value)}>
                <option value="">-- เลือกชั้น --</option>
                {classList.map(cls => <option key={cls.c_id} value={String(cls.c_id)}>{cls.c_class}</option>)}
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">โปรแกรม</label>
              <select className="form-select" value={String(selectedProgram)} onChange={e => setSelectedProgram(e.target.value)}>
                <option value="">-- เลือกโปรแกรม --</option>
                {programList.map(prog => <option key={prog.p_id} value={String(prog.p_id)}>{prog.p_code}</option>)}
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">หลักสูตรที่ใช้</label>
              <select className="form-select" value={String(selectedSyllabus)} onChange={e => setSelectedSyllabus(e.target.value)}>
                <option value="">-- เลือกหลักสูตร --</option>
                {curriculumList.map(syl => <option key={syl.syl_id} value={String(syl.syl_id)}>หลักสูตรปีการศึกษา {syl.syl_year}</option>)}
              </select>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>ปิด</Button>
          <Button variant="success" onClick={handleSaveAssignSyllabus}>
            {modalMode === "add" ? "บันทึก" : "บันทึกการแก้ไข"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Subject add/edit Modal */}
      <Modal show={subjectModalShow} backdrop="static" size="md" onHide={() => setSubjectModalShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="ri-book-2-line"></i> {subjectModalMode === "add" ? "เพิ่มรายวิชา" : "แก้ไขรายวิชา"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={e => e.preventDefault()}>
            <Form.Group className="mb-3">
              <Form.Label>รหัสวิชา</Form.Label>
              <Form.Control
                type="text"
                value={subForm.sub_subid}
                onChange={e => setSubForm({ ...subForm, sub_subid: e.target.value })}
                placeholder="เช่น ท21101"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>ชื่อวิชา</Form.Label>
              <Form.Control
                type="text"
                value={subForm.sub_subname}
                onChange={e => setSubForm({ ...subForm, sub_subname: e.target.value })}
                placeholder="ชื่อวิชา"
              />
            </Form.Group>
            <Row className="mb-3">
              <Col>
                <Form.Label>หน่วยกิต</Form.Label>
                <Form.Control
                  type="number"
                  step="0.5"
                  min="0"
                  value={subForm.sub_credit}
                  onChange={e => setSubForm({ ...subForm, sub_credit: e.target.value })}
                  placeholder="0.5"
                />
              </Col>
              <Col>
                <Form.Label>ลำดับ</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={subForm.sub_order}
                  onChange={e => setSubForm({ ...subForm, sub_order: e.target.value })}
                  placeholder="0"
                />
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Label>ภาคเรียน</Form.Label>
                <Form.Select
                  value={subForm.sub_semester}
                  onChange={e => setSubForm({ ...subForm, sub_semester: e.target.value })}
                >
                  <option value="1">ภาคเรียนที่ 1</option>
                  <option value="2">ภาคเรียนที่ 2</option>
                </Form.Select>
              </Col>
              <Col>
                <Form.Label>ประเภท</Form.Label>
                <Form.Select
                  value={subForm.sub_type}
                  onChange={e => setSubForm({ ...subForm, sub_type: e.target.value })}
                >
                  <option value="basic">พื้นฐาน</option>
                  <option value="extra">เพิ่มเติม</option>
                </Form.Select>
              </Col>
            </Row>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSubjectModalShow(false)}>ปิด</Button>
          <Button variant="success" onClick={handleSaveSubject}>
            {subjectModalMode === "add" ? "บันทึก" : "บันทึกการแก้ไข"}
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Import subjects modal */}
      <Modal show={importModalShow} backdrop="static" size="sm" onHide={() => setImportModalShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title><i className="ri-file-copy-line"></i> นำเข้ารายวิชาจากหลักสูตรอื่น</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted fs-sm mb-3">
            นำเข้ารายวิชาสำหรับ <strong>{selectedAssign?.c_class}</strong>
            {selectedAssign?.p_code && <> ({selectedAssign.p_code})</>} เข้าสู่หลักสูตรปี {selectedAssign?.syl_year}
            <br />รายวิชาที่มีรหัสซ้ำจะถูกข้ามโดยอัตโนมัติ
          </p>
          <Form.Group>
            <Form.Label>เลือกหลักสูตรต้นทาง</Form.Label>
            <Form.Select value={importSourceSylId} onChange={e => setImportSourceSylId(e.target.value)}>
              <option value="">-- เลือกหลักสูตร --</option>
              {curriculumList
                .filter(syl => String(syl.syl_id) !== String(selectedAssign?.syl_id))
                .map(syl => (
                  <option key={syl.syl_id} value={syl.syl_id}>หลักสูตรปีการศึกษา {syl.syl_year}</option>
                ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setImportModalShow(false)}>ปิด</Button>
          <Button variant="primary" onClick={handleImportSubjects}>
            <i className="ri-file-copy-line"></i> นำเข้า
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
}
